/**
 * One NEAR transaction, every receipt decoded (blackbeard/observer#44).
 *
 * An explorer shows the envelope — `bridge-mng.near` called `omft.near` — and
 * stops. What the call *did* is spread over a dozen receipts on four shards, in
 * base64 arguments and event logs, and that is what this page lays out: the
 * receipt tree in execution order, each call's arguments, each event with its
 * amounts in token units, and the bridge's memos read and linked back to this
 * chain.
 *
 * Nothing is summarised away. A failed receipt is shown failed with the
 * runtime's own words, and its logs are still listed — a failed receipt keeps
 * them, which is exactly why "succeeded" is the first thing to check.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import type { NearAction } from '../api/generated/NearAction'
import type { NearEvent } from '../api/generated/NearEvent'
import type { NearMemo } from '../api/generated/NearMemo'
import type { NearPart } from '../api/generated/NearPart'
import type { NearReceipt } from '../api/generated/NearReceipt'
import type { NearTx } from '../api/generated/NearTx'
import { RequestFailed, fetchNearTx } from '../api/rest'
import { height as fmtHeight, utc, when } from '../lib/format'
import { amountText, amountTitle, nearText, shortNear } from '../lib/near'
import { href } from '../lib/routes'
import { Field } from './Field'

const EXPLORER = 'https://nearblocks.io/txns/'

/** How often to re-read a transaction that is not yet kept. */
const PENDING_REFRESH_MS = 15_000

export function NearTxPanel({ chain, hash }: { chain: string; hash: string }) {
  const [tx, setTx] = useState<NearTx | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [refunds, setRefunds] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    let timer: number | undefined
    setTx(null)
    setError(null)
    const load = () =>
      fetchNearTx(chain, hash, controller.signal)
        .then((t) => {
          setTx(t)
          setError(null)
          // Kept means final: nothing about it can change, so stop asking.
          if (!t.stored) timer = window.setTimeout(load, PENDING_REFRESH_MS)
        })
        .catch((e: unknown) => {
          if (controller.signal.aborted) return
          setError(e instanceof RequestFailed ? e.message : 'Could not reach the observer.')
        })
    load()
    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [chain, hash])

  const shown = tx ? tx.receipts.filter((r) => refunds || !r.gas_refund) : []
  const hiddenRefunds = tx ? tx.receipts.filter((r) => r.gas_refund).length : 0
  const shards = tx ? new Set(tx.receipts.map((r) => r.shard).filter((s) => s !== null)) : new Set()
  const failed = tx ? tx.receipts.filter((r) => !r.succeeded).length : 0
  const withdrawals = tx
    ? uniqueMemos(
        tx.receipts.flatMap((r) => r.events.flatMap((e) => e.memos)),
        'withdraw_to',
      )
    : []

  return (
    <section className="panel" style={{ marginBottom: 26 }}>
      <div className="panel-head">
        <h2 className="panel-title">NEAR transaction</h2>
        <span className="eyebrow mono-wrap" title={hash}>
          {hash}
        </span>
      </div>

      {error && <p className="empty">{error}</p>}
      {!error && !tx && (
        <p className="empty">
          Reading the transaction from NEAR. The first read assembles it receipt by receipt from the
          nodes tracking each shard, which can take a few seconds; after that it is kept.
        </p>
      )}

      {tx && (
        <>
          <div className="node-grid fit">
            <Field label="Signed by" value={shortNear(tx.signer)} title={tx.signer} />
            <Field label="Sent to" value={shortNear(tx.receiver)} title={tx.receiver} />
            <Field
              label="Included"
              value={tx.height === null ? '—' : `#${fmtHeight(tx.height)}`}
              note={tx.at ? when(tx.at) : undefined}
              title={tx.at ? utc(tx.at) : undefined}
            />
            <Field
              label="Result"
              value={
                tx.succeeded
                  ? failed
                    ? 'succeeded, with a failed receipt'
                    : 'succeeded'
                  : 'failed'
              }
              note={tx.failure ?? undefined}
            />
            <Field
              label="Receipts"
              value={`${tx.receipts.length - hiddenRefunds}${hiddenRefunds ? ` + ${hiddenRefunds} gas refunds` : ''}`}
              note={
                shards.size
                  ? `across ${shards.size} shard${shards.size === 1 ? '' : 's'}`
                  : undefined
              }
            />
          </div>

          {tx.quantus_txs.length > 0 && (
            <p className="panel-note">
              {tx.quantus_txs.map((q) => (
                <span key={q.hash}>
                  Minted against Quantus transaction{' '}
                  {q.height !== null ? (
                    <Link to={href({ chain, tx: q.hash })} title={q.hash}>
                      {q.hash.slice(0, 12)}… in block #{fmtHeight(q.height)}
                      {q.extrinsic_index !== null ? `, extrinsic ${q.extrinsic_index}` : ''}
                    </Link>
                  ) : (
                    <span title={q.hash}>
                      {q.hash.slice(0, 12)}… (not in this observer&apos;s index)
                    </span>
                  )}
                  . The bridge writes that hash into the mint itself, so this link is exact.{' '}
                </span>
              ))}
            </p>
          )}
          {withdrawals.length > 0 && (
            <p className="panel-note">
              {withdrawals.map((m) => (
                <span key={m.text}>
                  A withdrawal to Quantus address{' '}
                  <Link to={href({ chain, account: m.address ?? '' })}>{m.address}</Link>. The
                  payout on Quantus carries no NEAR hash, so the bridge page matches it by
                  rule.{' '}
                </span>
              ))}
            </p>
          )}

          <ol className="receipt-tree">
            {shown.map((r) => (
              <Receipt key={r.id} chain={chain} receipt={r} tx={tx} />
            ))}
          </ol>

          {hiddenRefunds > 0 && (
            <div className="pager" style={{ justifyContent: 'flex-start' }}>
              <button
                className="segmented panel-button"
                aria-pressed={refunds}
                onClick={() => setRefunds((v) => !v)}
              >
                {refunds ? 'Hide' : 'Show'} {hiddenRefunds} gas refund
                {hiddenRefunds === 1 ? '' : 's'}
              </button>
            </div>
          )}

          <p className="panel-note">
            Read from {tx.source === 'own' ? 'our own NEAR nodes' : 'a public NEAR node'}
            {tx.stored
              ? ', and kept: it is complete and every block it touched is final.'
              : tx.complete
                ? '. Not final yet, so it is read again until it is.'
                : '. Some receipts have not executed yet, so it is read again until they have.'}{' '}
            A gas refund is the protocol returning unused gas to the signer. Each receipt executes
            on the shard of the account it is sent to, a block or more after the one that created
            it. Amounts are in each token&apos;s units, from its own metadata; hover for the exact
            figure and the contract. Also on{' '}
            <a href={`${EXPLORER}${tx.hash}`} target="_blank" rel="noreferrer">
              nearblocks
            </a>
            .
          </p>
        </>
      )}
    </section>
  )
}

function uniqueMemos(memos: NearMemo[], kind: string): NearMemo[] {
  const out: NearMemo[] = []
  for (const m of memos) if (m.kind === kind && !out.some((o) => o.text === m.text)) out.push(m)
  return out
}

function Receipt({ chain, receipt: r, tx }: { chain: string; receipt: NearReceipt; tx: NearTx }) {
  return (
    <li className="receipt" style={{ marginLeft: `${Math.min(r.depth, 8) * 18}px` }}>
      <div className="receipt-head">
        <span title={r.predecessor}>{shortNear(r.predecessor)}</span>
        <span className="receipt-arrow" aria-label="to">
          →
        </span>
        <strong title={r.receiver}>{shortNear(r.receiver)}</strong>
        {!r.succeeded && <span className="chip chip-failed">failed</span>}
        {r.gas_refund && <span className="chip chip-submitted">gas refund</span>}
        <span className="receipt-meta">
          {r.shard !== null && `shard ${r.shard} · `}
          {r.height !== null && `#${fmtHeight(r.height)}`}
          {r.at && (
            <span title={utc(r.at)}>
              {' · '}
              {when(r.at)}
            </span>
          )}
        </span>
      </div>

      {r.failure && <div className="receipt-failure">{r.failure}</div>}

      {r.actions.map((a, i) => (
        <Action key={i} chain={chain} action={a} tx={tx} />
      ))}

      {r.events.map((e, i) => (
        <Event key={i} chain={chain} event={e} tx={tx} />
      ))}

      {r.logs.length > 0 && (
        <details className="receipt-line">
          <summary className="receipt-meta">
            {r.logs.length} other log line{r.logs.length === 1 ? '' : 's'}
          </summary>
          <pre className="receipt-json">{r.logs.join('\n')}</pre>
        </details>
      )}
    </li>
  )
}

/**
 * A call: in words when the decoder could account for every argument, as its
 * arguments otherwise. Either way the arguments themselves are one click away,
 * because they are the source and the sentence is only a reading of them
 * (blackbeard/observer#47).
 */
function Action({ chain, action: a, tx }: { chain: string; action: NearAction; tx: NearTx }) {
  if (a.kind !== 'FunctionCall') {
    return (
      <div className="receipt-line">
        <code>{a.kind}</code>
        {a.deposit && <span className="receipt-meta"> · {nearText(a.deposit)}</span>}
      </div>
    )
  }
  return (
    <div className="receipt-line">
      <details>
        <summary>
          <code>{a.method}</code>
          {a.sentence ? (
            <span className="near-sentence">
              <Sentence chain={chain} parts={a.sentence} tx={tx} />
            </span>
          ) : (
            a.memos.map((m) => (
              <span key={m.text} className="chip chip-submitted" title={m.text}>
                {m.kind.replace('_', ' ')}
              </span>
            ))
          )}
          {a.deposit && <span className="receipt-meta"> · {nearText(a.deposit)} attached</span>}
        </summary>
        {a.args !== null ? (
          <pre className="receipt-json">{JSON.stringify(a.args, null, 2)}</pre>
        ) : (
          <p className="panel-note">{a.args_bytes ?? 0} bytes of arguments that are not JSON.</p>
        )}
      </details>
      {!a.sentence && a.args !== null && (
        <div className="receipt-meta">
          arguments in a shape this site does not read — shown as sent
        </div>
      )}
    </div>
  )
}

/** An event: one sentence per entry when every field was accounted for. */
function Event({ chain, event: e, tx }: { chain: string; event: NearEvent; tx: NearTx }) {
  if (e.sentences) {
    return (
      <div className="receipt-line">
        <details>
          <summary>
            <span className="event-key">{e.standard}</span> <code>{e.event}</code>
            {e.sentences.map((parts, i) => (
              <span
                key={i}
                className={`near-sentence${e.sentences!.length > 1 ? ' near-sentence-line' : ''}`}
              >
                <Sentence chain={chain} parts={parts} tx={tx} />
              </span>
            ))}
          </summary>
          <pre className="receipt-json">{JSON.stringify(e.data, null, 2)}</pre>
        </details>
      </div>
    )
  }
  return <RawEvent chain={chain} event={e} tx={tx} />
}

/** The pieces of a sentence, spaced as words and linked where they can be. */
function Sentence({ chain, parts, tx }: { chain: string; parts: NearPart[]; tx: NearTx }) {
  return (
    <>
      {parts.map((p, i) => {
        // A word run that opens with punctuation sits against what precedes it.
        const space = i > 0 && !(p.kind === 'text' && /^[,;]/.test(p.text)) ? ' ' : ''
        return (
          <span key={i}>
            {space}
            <Part chain={chain} part={p} tx={tx} />
          </span>
        )
      })}
    </>
  )
}

function Part({ chain, part: p, tx }: { chain: string; part: NearPart; tx: NearTx }) {
  switch (p.kind) {
    case 'text':
      return <>{p.text}</>
    case 'amount':
      return (
        <strong className="near-amount" title={amountTitle(p.amount, p.token, tx.tokens)}>
          {amountText(p.amount, p.token, tx.tokens)}
        </strong>
      )
    case 'near':
      return <strong className="near-amount">{nearText(p.yocto)}</strong>
    case 'account':
      return p.intents ? (
        <Link to={href({ chain, intentsAccount: p.account })} title={p.account}>
          {shortNear(p.account)}
        </Link>
      ) : (
        <span className="near-account" title={p.account}>
          {shortNear(p.account)}
        </span>
      )
    case 'quantus_tx': {
      const known = tx.quantus_txs.find((q) => q.hash === p.hash)
      const label = `${p.hash.slice(0, 10)}…`
      return known?.height != null ? (
        <Link to={href({ chain, tx: p.hash })} title={p.hash}>
          {label} (block #{fmtHeight(known.height)})
        </Link>
      ) : (
        <span title={p.hash}>{label}</span>
      )
    }
    case 'quantus_address':
      return (
        <Link to={href({ chain, account: p.address })} title={p.address}>
          {shortNear(p.address)}
        </Link>
      )
  }
}

function RawEvent({ chain, event: e, tx }: { chain: string; event: NearEvent; tx: NearTx }) {
  return (
    <div className="receipt-line">
      <details>
        <summary>
          <span className="event-key">{e.standard}</span> <code>{e.event}</code>
          {e.amounts.length > 0 && (
            <span className="event-fields" style={{ marginLeft: 8 }}>
              {e.amounts.map((a, i) => (
                <span
                  key={i}
                  className="event-field"
                  title={amountTitle(a.amount, a.token, tx.tokens)}
                >
                  {amountText(a.amount, a.token, tx.tokens)}
                  {a.account && (
                    <span className="receipt-meta" title={a.account}>
                      {' '}
                      · {shortNear(a.account)}
                    </span>
                  )}
                </span>
              ))}
            </span>
          )}
          {e.memos.map((m) => (
            <span key={m.text} className="chip chip-submitted" title={m.text}>
              {m.kind.replace('_', ' ')}
            </span>
          ))}
        </summary>
        <pre className="receipt-json">{JSON.stringify(e.data, null, 2)}</pre>
      </details>
      {e.intents_accounts.length > 0 && (
        <div className="receipt-meta">
          Intents account{e.intents_accounts.length === 1 ? '' : 's'}:{' '}
          {e.intents_accounts.map((a, i) => (
            <span key={a}>
              {i > 0 && ', '}
              <Link to={href({ chain, intentsAccount: a })} title={a}>
                {shortNear(a)}
              </Link>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
